/**
 * The recurring submit branch the four create forms share — ONE chain so the
 * expense, AP, AR and journal forms cannot drift:
 *
 *   payload = buildRecurringPayload(type, build*CreateBody(data))   (in the form)
 *   input   = toCreateTemplateInput(type, recurrence, payload)
 *   create  → if the response says `due_now`, chain `run` so the first
 *             document appears immediately (Decisions: "Saving a template
 *             whose start is org-today chains Run now")
 *           → `orgNavigate('/accounting/recurring', { replace: true })`
 *             BEFORE the caller closes the dialog (`docs/FULLSCREEN_FORM_MODALS.md`
 *             "Closing, history, and navigating after submit") — unless the
 *             host passes `navigateOnSuccess: false` (the recurring page is
 *             already there, like the Vault's own Upload button)
 *   update  (template-edit mode) → PATCH, close in place, no run, no navigate.
 *
 * Attachment / carbon / budget-tag saves are the CALLER's side channels and
 * are skipped on this branch (nothing was created to attach them to; v1 does
 * not copy them to generated documents).
 *
 * Errors: the mutations toast their own failures (`useRecurringTemplates`,
 * incl. the 409 `busy` case) and the app-level `MutationCache.onError` reports
 * them to Sentry — nothing is captured twice here; each step drops a
 * breadcrumb so a failure mid-chain is legible in the event. A failed `run`
 * after a successful create is swallowed on purpose: the template EXISTS and
 * the row on the recurring page shows its error state, so the chain still
 * navigates there.
 */
import { useCallback } from 'react';
import type { RecurringDocumentType, RecurringTemplatePayloadByType } from '@releve/shared';
import { useOrgNavigate } from '@/hooks/useOrgNavigate';
import {
  useCreateRecurringTemplate,
  useRunRecurringTemplate,
  useUpdateRecurringTemplate,
} from '@/hooks/useRecurringTemplates';
import { addBreadcrumb } from '@/lib/sentry';
import { toCreateTemplateInput, type RecurrenceFormValues } from '@/lib/recurrence';

/** Where the create chain lands the user. */
export const RECURRING_LANDING_PATH = '/accounting/recurring';

export interface RecurringTemplateSubmitArgs<T extends RecurringDocumentType> {
  organizationId: string;
  documentType: T;
  recurrence: RecurrenceFormValues;
  /** `buildRecurringPayload(documentType, build*CreateBody(data))`. */
  payload: RecurringTemplatePayloadByType[T];
  /** Template-edit mode → PATCH this template instead of creating one. */
  templateId?: string | null;
  /** `false` when the host is already on the recurring page. Create only. */
  navigateOnSuccess?: boolean;
}

export interface RecurringTemplateSubmitResult {
  templateId: string;
  /** Whether the due-now run was chained AND succeeded. */
  ran: boolean;
}

export function useRecurringTemplateSubmit() {
  const createTemplate = useCreateRecurringTemplate();
  const updateTemplate = useUpdateRecurringTemplate();
  const runTemplate = useRunRecurringTemplate();
  const orgNavigate = useOrgNavigate();

  const isPending = createTemplate.isPending || updateTemplate.isPending || runTemplate.isPending;
  // `mutateAsync` is referentially stable across renders (the result object is not).
  const { mutateAsync: createAsync } = createTemplate;
  const { mutateAsync: updateAsync } = updateTemplate;
  const { mutateAsync: runAsync } = runTemplate;

  const submit = useCallback(
    async <T extends RecurringDocumentType>(
      args: RecurringTemplateSubmitArgs<T>,
    ): Promise<RecurringTemplateSubmitResult> => {
      const { organizationId, documentType, recurrence, payload, templateId, navigateOnSuccess } = args;
      const input = toCreateTemplateInput(documentType, recurrence, payload);

      if (templateId) {
        addBreadcrumb('recurring: update template', { organizationId, templateId, documentType });
        await updateAsync({ organizationId, templateId, data: input });
        return { templateId, ran: false };
      }

      addBreadcrumb('recurring: create template', { organizationId, documentType, start_date: input.start_date });
      const created = await createAsync({ organizationId, data: input });
      const createdId = created.template.id;

      let ran = false;
      if (created.due_now) {
        addBreadcrumb('recurring: run due-now template', { organizationId, templateId: createdId });
        try {
          await runAsync({ organizationId, templateId: createdId, documentType });
          ran = true;
        } catch {
          // Toasted by the run mutation; the template exists and its row
          // carries the error — the chain still lands on the list.
        }
      }

      if (navigateOnSuccess !== false) {
        addBreadcrumb('recurring: navigate to list', { organizationId, templateId: createdId });
        orgNavigate(RECURRING_LANDING_PATH, { replace: true });
      }
      return { templateId: createdId, ran };
    },
    [createAsync, updateAsync, runAsync, orgNavigate],
  );

  return { submit, isPending };
}
